import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from '../../../interceptors/axios';
import { glassSx } from '../../../theme/sx';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (screenId: number) => void;
  initialCode?: string;
}


const IcoX: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IcoScreen: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const ActivationModal: React.FC<Props> = ({ open, onClose, onSuccess, initialCode = '' }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setCode(initialCode.replace(/\D/g, '').slice(0, 9)); setError(''); }
    else { setCode(''); setError(''); }
  }, [open, initialCode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleActivate = async () => {
    if (code.length !== 9) { setError('Code must be exactly 9 digits'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post<{ id: number; device_id: string }>('/screens/activate', { activation_code: code });
      onSuccess(res.data.id);
      onClose();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Activation failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: 'rgba(0,0,0,.55)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Box sx={{
        width: 380, maxWidth: '95vw', borderRadius: '16px', overflow: 'hidden',
        ...glassSx,
        boxShadow: '0 24px 64px rgba(0,0,0,.6)',
        animation: 'modalIn .18s ease',
        '@keyframes modalIn': {
          from: { opacity: 0, transform: 'scale(.96) translateY(8px)' },
          to:   { opacity: 1, transform: 'scale(1) translateY(0)' },
        },
      }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '20px', py: '16px', borderBottom: '1px solid var(--divider)' }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(var(--accent-rgb,99,102,241),.25), rgba(var(--accent-rgb,99,102,241),.1))',
            border: '1px solid var(--glass-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent)',
          }}>
            <IcoScreen />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
              {t('screens.activateTitle')}
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: '7px', cursor: 'pointer',
              color: 'var(--text-disabled)', border: '1px solid transparent', background: 'none',
              '&:hover': { bgcolor: 'var(--glass-hover)', borderColor: 'var(--glass-border)', color: 'var(--text-primary)' },
              transition: 'all .12s',
            }}
          >
            <IcoX />
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ px: '20px', pt: '18px', pb: '20px' }}>
          <Typography sx={{ fontSize: 13, color: 'var(--text-secondary)', mb: '16px', lineHeight: 1.5 }}>
            {t('screens.activateDesc')}
          </Typography>

          {/* Code input */}
          <Box
            component="input"
            value={code}
            autoFocus
            maxLength={9}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 9);
              setCode(v);
              setError('');
            }}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && code.length === 9) handleActivate(); }}
            placeholder="000 000 000"
            sx={{
              width: '100%', boxSizing: 'border-box',
              background: 'rgba(255,255,255,.04)',
              border: error ? '1px solid rgba(248,113,113,.6)' : '1px solid var(--glass-border)',
              borderRadius: '10px', px: '16px', py: '13px',
              fontSize: 22, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace',
              color: 'var(--text-primary)', letterSpacing: '6px', textAlign: 'center',
              outline: 'none',
              '&:focus': { borderColor: error ? 'rgba(248,113,113,.8)' : 'var(--accent)' },
              '&::placeholder': { color: 'var(--text-disabled)', letterSpacing: '4px', fontWeight: 400 },
              transition: 'border-color .15s',
              mb: '8px',
            }}
          />

          {/* Progress dots */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '5px', mb: '16px' }}>
            {Array.from({ length: 9 }).map((_, i) => (
              <Box key={i} sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: i < code.length ? 'var(--accent)' : 'rgba(255,255,255,.12)',
                transition: 'background-color .1s',
              }} />
            ))}
          </Box>

          {/* Error */}
          {error && (
            <Box sx={{ mb: '16px', px: '12px', py: '9px', borderRadius: '8px', bgcolor: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.25)' }}>
              <Typography sx={{ fontSize: 12, color: '#f87171' }}>{error}</Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Box
              component="button"
              onClick={onClose}
              sx={{
                flex: 1, py: '9px', borderRadius: '9px', cursor: 'pointer',
                ...glassSx, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500,
                '&:hover': { color: 'var(--text-primary)', borderColor: 'var(--glass-border-strong)' },
                transition: 'color .12s, border-color .12s',
              }}
            >
              {t('common.cancel')}
            </Box>
            <Box
              component="button"
              onClick={handleActivate}
              sx={{
                flex: 2, py: '9px', borderRadius: '9px',
                cursor: loading || code.length !== 9 ? 'default' : 'pointer',
                bgcolor: 'var(--accent)', border: '1px solid var(--accent)',
                color: '#fff', fontSize: 13, fontWeight: 600,
                opacity: loading || code.length !== 9 ? .5 : 1,
                transition: 'opacity .15s',
                pointerEvents: loading ? 'none' : 'auto',
              }}
            >
              {loading ? '…' : t('screens.activateBtn')}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ActivationModal;
